/**
 * PublicBusiness - Publiczna strona firmy
 * Styl Katzenberg: dark, elegant, gold accents, serif headings
 * CSS Variables for easy theme switching (dark default, light V2)
 * SEO + Schema.org + conversion-focused
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, Check, Star, ChevronRight, Loader2, Menu, X, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';
import { BusinessUnclaimedBanner } from '@/components/BusinessComplianceNotice';
import UnclaimedInterest from '@/components/UnclaimedInterest';
import NewPageOnboarding from '@/components/NewPageOnboarding';
import PublishedHeroBlock from '@/components/PublishedHeroBlock';
import Breadcrumbs from '@/components/Breadcrumbs';
import { track } from '@/utils/track';

import { API_BASE as API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

/* ------------------------------------------------------------------ */
/*  SEO helper: direct DOM meta management (reliable in all configs)   */
/* ------------------------------------------------------------------ */
function usePageSEO({ title, description, ogTitle, ogDescription, ogType, ogImage, ogImageAlt, canonical, schemas }) {
  const schemasKey = JSON.stringify(schemas || null);
  useEffect(() => {
    if (!title) return;
    document.title = title;

    const setMeta = (attr, attrVal, content) => {
      let el = document.querySelector(`meta[${attr}="${attrVal}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, attrVal); document.head.appendChild(el); }
      el.setAttribute('content', content);
      el.setAttribute('data-pb', 'true');
    };

    setMeta('name', 'description', description || '');
    setMeta('property', 'og:title', ogTitle || title);
    setMeta('property', 'og:description', ogDescription || description || '');
    setMeta('property', 'og:type', ogType || 'website');
    if (ogImage) {
      setMeta('property', 'og:image', ogImage);
      setMeta('name', 'twitter:card', 'summary_large_image');
      setMeta('name', 'twitter:image', ogImage);
      if (ogImageAlt) {
        setMeta('property', 'og:image:alt', ogImageAlt);
        setMeta('name', 'twitter:image:alt', ogImageAlt);
      }
    }

    // Canonical
    let link = document.querySelector('link[rel="canonical"][data-pb]');
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; link.setAttribute('data-pb', 'true'); document.head.appendChild(link); }
    link.href = canonical || window.location.href;

    // Schema.org JSON-LD (obsługa wielu bloków: LocalBusiness + Breadcrumb + FAQ)
    document.querySelectorAll('script[data-pb="schema"]').forEach(el => el.remove());
    const list = Array.isArray(schemas) ? schemas : (schemas ? [schemas] : []);
    list.forEach(obj => {
      if (!obj) return;
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-pb', 'schema');
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.querySelectorAll('[data-pb]').forEach(el => el.remove());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, ogTitle, ogDescription, ogType, ogImage, ogImageAlt, canonical, schemasKey]);
}

/* ------------------------------------------------------------------ */
/*  SEO builders — CTR-optimized title/description + Schema.org        */
/*  (liczone w czasie renderu; nadpisują słabe auto-seo z bazy)        */
/* ------------------------------------------------------------------ */
const SITE = 'https://apisai.pl';

function buildBizSeoTitle(biz) {
  const name = (biz.name || '').trim();
  const city = (biz.city || '').trim();
  const cityPart = city && !name.toLowerCase().includes(city.toLowerCase()) ? ` ${city}` : '';
  const base = `${name}${cityPart}`;
  const suffix = biz.source === 'google_places' ? ' | Kontakt, Oferta' : ' | Opinie, Kontakt, Oferta';
  return (base.length + suffix.length <= 60) ? base + suffix : base;
}

function buildBizSeoDesc(biz) {
  const name = (biz.name || '').trim();
  const city = (biz.city || '').trim();
  const loc = city && !name.toLowerCase().includes(city.toLowerCase()) ? `, ${city}` : '';
  const rating = biz.google_rating;
  const count = biz.google_rating_count;
  // Ocena Google ukrywana po stronie backendu dla danych z Google Places (rating=null)
  const ratingPart = rating ? ` Ocena Google ${rating}/5${count ? ` (${count} opinii)` : ''}.` : '';
  let desc = `${name}${loc} — oferta, godziny otwarcia i dane kontaktowe.${ratingPart} Napisz lub zadzwoń.`;
  if (desc.length > 160) desc = desc.slice(0, 159).replace(/\s+\S*$/, '').trim();
  return desc;
}

function buildBizFaqItems(biz) {
  const name = (biz.name || 'Firma').trim();
  const city = (biz.city || '').trim();
  const items = [];
  const cBits = [];
  if (biz.phone) cBits.push(`telefonicznie pod numerem ${biz.phone}`);
  if (biz.email) cBits.push(`mailowo: ${biz.email}`);
  cBits.push('przez formularz kontaktowy na tej stronie');
  items.push({ q: `Jak skontaktować się z ${name}?`, a: `Z ${name} skontaktujesz się ${cBits.join(', ')}.` });
  if (biz.opening_hours) {
    items.push({ q: `W jakich godzinach otwarte jest ${name}?`, a: String(biz.opening_hours).replace(/\s*\|\s*/g, '\n') });
  }
  if (biz.address || city) {
    const where = biz.address ? `pod adresem ${biz.address}` : `w miejscowości ${city}`;
    const voiv = biz.voivodeship ? `, województwo ${biz.voivodeship}` : '';
    items.push({ q: `Gdzie znajduje się ${name}?`, a: `${name} znajduje się ${where}${voiv}, Polska.` });
  }
  const services = biz.services || [];
  if (services.length) {
    const slist = services.slice(0, 6).map(s => s.name).filter(Boolean).join(', ');
    items.push({ q: `Jakie usługi oferuje ${name}?`, a: `Oferta obejmuje m.in.: ${slist}. Skontaktuj się, aby poznać szczegóły i ceny.` });
  } else {
    const ind = (biz.industry || biz.category || '').toLowerCase();
    items.push({ q: `Czym zajmuje się ${name}?`, a: `${name} to ${ind || 'firma'}${city ? ` w miejscowości ${city}` : ''}. Skontaktuj się, aby poznać pełną ofertę i ceny.` });
  }
  return items;
}

function buildBizSchemas(biz, faqItems, canonical) {
  const name = biz.name || 'Firma';
  const localBiz = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    description: biz.short_description || biz.description || undefined,
    url: canonical,
    ...(biz.phone && { telephone: biz.phone }),
    ...(biz.email && { email: biz.email }),
    ...((biz.address || biz.city) && {
      address: {
        '@type': 'PostalAddress',
        ...(biz.address && { streetAddress: biz.address }),
        ...(biz.city && { addressLocality: biz.city }),
        ...(biz.voivodeship && { addressRegion: biz.voivodeship }),
        addressCountry: 'PL',
      },
    }),
    ...(biz.google_rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: biz.google_rating,
        ...(biz.google_rating_count && { reviewCount: biz.google_rating_count }),
        bestRating: 5,
      },
    }),
    ...(biz.price_range && { priceRange: biz.price_range }),
  };
  const sameAs = [biz.website, biz.facebook, biz.instagram, biz.google_maps_url].filter(Boolean);
  if (sameAs.length) localBiz.sameAs = sameAs;
  const services = biz.services || [];
  if (services.length) {
    localBiz.hasOfferCatalog = {
      '@type': 'OfferCatalog', name: 'Usługi',
      itemListElement: services.slice(0, 20).map(s => ({
        '@type': 'Offer', name: s.name,
        ...(s.description && { description: s.description }),
        ...(s.price && { price: s.price, priceCurrency: 'PLN' }),
      })),
    };
  }
  const img = biz.cover_photo || (biz.photos && biz.photos[0]) || biz.logo;
  if (img) localBiz.image = img;

  const crumbs = [
    { '@type': 'ListItem', position: 1, name: 'APISAI', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Katalog firm', item: `${SITE}/katalog` },
  ];
  if (biz.category && biz.category_slug) {
    crumbs.push({ '@type': 'ListItem', position: 3, name: biz.category, item: `${SITE}/katalog/${biz.category_slug}` });
  }
  crumbs.push({ '@type': 'ListItem', position: crumbs.length + 1, name, item: canonical });
  const breadcrumb = { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: crumbs };

  const out = [localBiz, breadcrumb];
  if (faqItems && faqItems.length) {
    out.push({
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faqItems.map(it => ({
        '@type': 'Question', name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  INLINE STYLES (CSS-in-JS for self-contained public page)          */
/* ------------------------------------------------------------------ */

/* Accent color palettes */
const ACCENT_PALETTES = {
  gold:   { main: '#c9a96e', hover: '#d4b87a', borderHover: 'rgba(201,169,110,0.3)' },
  blue:   { main: '#6e8ec9', hover: '#82a0d4', borderHover: 'rgba(110,142,201,0.3)' },
  green:  { main: '#6ec98a', hover: '#82d49a', borderHover: 'rgba(110,201,138,0.3)' },
  red:    { main: '#c96e6e', hover: '#d48282', borderHover: 'rgba(201,110,110,0.3)' },
  purple: { main: '#8e6ec9', hover: '#a082d4', borderHover: 'rgba(142,110,201,0.3)' },
  teal:   { main: '#6ec9b8', hover: '#82d4c6', borderHover: 'rgba(110,201,184,0.3)' },
  'pastel-pink':     { main: '#f4b8c1', hover: '#f7cad1', borderHover: 'rgba(244,184,193,0.3)' },
  'pastel-blue':     { main: '#a8c8e8', hover: '#bbd5ee', borderHover: 'rgba(168,200,232,0.3)' },
  'pastel-mint':     { main: '#a8dbc5', hover: '#bbe6d3', borderHover: 'rgba(168,219,197,0.3)' },
  'pastel-lavender': { main: '#c5b8d9', hover: '#d3c8e3', borderHover: 'rgba(197,184,217,0.3)' },
  'pastel-peach':    { main: '#f4c9a8', hover: '#f7d6bb', borderHover: 'rgba(244,201,168,0.3)' },
  'pastel-yellow':   { main: '#f0e0a0', hover: '#f4e7b5', borderHover: 'rgba(240,224,160,0.3)' },
};

function buildThemeVars(themeKey, accentKey, fontKey) {
  const accent = ACCENT_PALETTES[accentKey] || ACCENT_PALETTES.gold;
  const fonts = fontKey === 'modern'
    ? { serif: "'Space Grotesk', system-ui, sans-serif", sans: "'Inter', system-ui, sans-serif" }
    : { serif: "'Cormorant Garamond', Georgia, 'Times New Roman', serif", sans: "'Manrope', system-ui, sans-serif" };
  const shared = {
    '--pb-accent': accent.main,
    '--pb-accent-hover': accent.hover,
    '--pb-border-hover': accent.borderHover,
    '--pb-serif': fonts.serif,
    '--pb-sans': fonts.sans,
  };
  if (themeKey === 'light') {
    return { ...shared,
      '--pb-bg': '#fafaf8', '--pb-bg-alt': '#f0efe8', '--pb-bg-card': '#ffffff',
      '--pb-text': '#1a1a1a', '--pb-text-muted': '#666666', '--pb-text-dim': '#999999',
      '--pb-border': '#e5e2d8', '--pb-input-bg': '#ffffff', '--pb-input-border': '#d5d2c8',
      '--pb-nav-bg': 'rgba(250,250,248,0.9)', '--pb-overlay': 'rgba(250,250,248,0.95)',
    };
  }
  return { ...shared,
    '--pb-bg': '#060606', '--pb-bg-alt': '#0c0c0c', '--pb-bg-card': '#111111',
    '--pb-text': '#f5f5f0', '--pb-text-muted': '#888888', '--pb-text-dim': '#555555',
    '--pb-border': '#1a1a1a', '--pb-input-bg': '#0e0e0e', '--pb-input-border': '#222222',
    '--pb-nav-bg': 'rgba(6,6,6,0.85)', '--pb-overlay': 'rgba(6,6,6,0.92)',
  };
}

/* ------------------------------------------------------------------ */
/*  Fade-in on scroll hook                                             */
/* ------------------------------------------------------------------ */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function FadeSection({ children, className = '', style = {}, ...rest }) {
  const ref = useFadeIn();
  return (
    <div ref={ref} className={className} style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)', ...style }} {...rest}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Gallery Lightbox                                                    */
/* ------------------------------------------------------------------ */
function Lightbox({ images, index, onClose, bizName }) {
  const [cur, setCur] = useState(index);
  useEffect(() => { setCur(index); }, [index]);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') setCur(c => Math.min(c + 1, images.length - 1)); if (e.key === 'ArrowLeft') setCur(c => Math.max(c - 1, 0)); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'var(--pb-overlay)', backdropFilter: 'blur(20px)' }} onClick={onClose} data-testid="lightbox-overlay">
      <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full" style={{ color: 'var(--pb-text-muted)' }} data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      <div className="relative max-w-5xl w-full mx-4" onClick={e => e.stopPropagation()}>
        <img src={getImageUrl(images[cur])} alt={`${bizName} — ${cur + 1}`} className="w-full max-h-[80vh] object-contain rounded-lg" />
        <div className="flex items-center justify-center gap-6 mt-6">
          <button onClick={() => setCur(c => Math.max(c - 1, 0))} disabled={cur === 0} className="px-4 py-2 text-sm rounded-full disabled:opacity-20" style={{ border: '1px solid var(--pb-border)', color: 'var(--pb-text-muted)' }} data-testid="lightbox-prev">Poprzednie</button>
          <span style={{ color: 'var(--pb-text-dim)', fontSize: '13px', letterSpacing: '0.1em' }}>{cur + 1} / {images.length}</span>
          <button onClick={() => setCur(c => Math.min(c + 1, images.length - 1))} disabled={cur === images.length - 1} className="px-4 py-2 text-sm rounded-full disabled:opacity-20" style={{ border: '1px solid var(--pb-border)', color: 'var(--pb-text-muted)' }} data-testid="lightbox-next">Nastepne</button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */
export default function PublicBusiness() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [similar, setSimilar] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/business/public/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then((data) => {
        setBiz(data);
        // Track view (dedup 1h per browser session)
        if (data && data.id) {
          track('view_business', {
            entity_type: 'business',
            entity_id: data.id,
            slug: data.slug || slug,
            user_id: data.user_id || '',
            name: data.name || '',
          });
        }
      })
      .catch(() => setBiz(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Linkowanie wewnętrzne (SEO): firmy z tej branży / miasta / województwa
  useEffect(() => {
    let active = true;
    fetch(`${API}/api/business/public/${slug}/similar`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (active) setSimilar(d && Array.isArray(d.groups) ? d.groups : []); })
      .catch(() => { if (active) setSimilar([]); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || (!formData.email && !formData.phone)) {
      toast.error('Podaj imie oraz email lub telefon');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/business/public/${slug}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) { setSent(true); toast.success('Wiadomosc wyslana!'); }
      else toast.error('Nie udało się wysłać');
    } catch { toast.error('Blad polaczenia'); }
    finally { setSending(false); }
  };

  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);

  /* ---- SEO (must be before early returns - rules of hooks) ---- */
  const allServices = biz?.services || [];
  const hasPhotos = biz?.photos?.length > 0;
  const canonicalUrl = biz ? `${SITE}/firma/${slug}` : '';
  const seoTitle = biz ? buildBizSeoTitle(biz) : null;
  const seoDesc = biz ? buildBizSeoDesc(biz) : null;
  const faqItems = biz ? buildBizFaqItems(biz) : [];
  const seoSchemas = biz ? buildBizSchemas(biz, faqItems, canonicalUrl) : null;
  const bizRawImg = biz && (biz.cover_photo || biz.photos?.[0] || biz.logo);
  const bizSeoImage = bizRawImg ? getImageUrl(bizRawImg) : null;
  const bizImageAlt = biz ? `${biz.name}${biz.city ? ' – ' + biz.city : ''}` : '';

  usePageSEO({
    title: seoTitle, description: seoDesc,
    ogTitle: seoTitle, ogDescription: seoDesc, ogType: 'business.business',
    ogImage: bizSeoImage, ogImageAlt: bizImageAlt,
    canonical: canonicalUrl, schemas: seoSchemas,
  });

  /* ---- Loading / Not found ---- */
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#060606' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#c9a96e' }} />
    </div>
  );

  if (!biz) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#060606', color: '#f5f5f0' }}>
      <h1 style={{ fontSize: '2rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>Strona nie znaleziona</h1>
      <p style={{ color: '#666' }}>Ta firma nie istnieje lub nie jest aktywna.</p>
    </div>
  );

  /* ---- Theme ---- */
  const vars = buildThemeVars(biz.theme || 'dark', biz.accent_color || 'gold', biz.font_style);
  const isDark = biz.theme !== 'light';
  // RODO: wizytówka nieprzejęta (import publiczny / Google Places) — bez formularza leadów
  const isUnclaimed = biz.is_claimed === false;

  /* ---- Section order ---- */
  const sectionOrder = biz.section_order || ['uslugi', 'galeria', 'o-nas', 'kontakt'];
  const SECTION_NAV_LABELS = { 'uslugi': 'Usługi', 'galeria': 'Galeria', 'o-nas': 'O nas', 'kontakt': 'Kontakt' };

  const navItems = [
    { id: 'start', label: 'Start' },
    ...sectionOrder
      .filter(sec => {
        if (sec === 'uslugi') return allServices.length > 0;
        if (sec === 'galeria') return hasPhotos;
        if (sec === 'o-nas') return !!biz.description;
        return true;
      })
      .map(sec => ({ id: sec, label: SECTION_NAV_LABELS[sec] || sec })),
    ...(faqItems.length ? [{ id: 'faq', label: 'FAQ' }] : []),
  ];

  /* ---- Shared inline styles ---- */
  const sectionPad = { padding: '120px 24px', maxWidth: '1100px', margin: '0 auto' };
  const labelStyle = { fontSize: '11px', letterSpacing: '0.35em', textTransform: 'uppercase', color: 'var(--pb-accent)', fontFamily: 'var(--pb-sans)', fontWeight: 500, marginBottom: '16px' };
  const h2Style = { fontFamily: 'var(--pb-serif)', fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 400, color: 'var(--pb-text)', lineHeight: 1.15, margin: '0 0 48px 0', letterSpacing: '-0.02em' };
  const bodyText = { color: 'var(--pb-text-muted)', fontSize: '15px', lineHeight: 1.75, fontFamily: 'var(--pb-sans)' };

  return (
    <>
      {lightbox !== null && hasPhotos && (
        <Lightbox images={biz.photos} index={lightbox} onClose={() => setLightbox(null)} bizName={biz.name} />
      )}

      {/* Published Hero z Kreatora pierwszego wrażenia */}
      <PublishedHeroBlock entityType="business" slug={slug} />

      <NewPageOnboarding kind="business" />

      {/* RODO / transparentność — disclaimer + akcje (Przejmij / Popraw / Usuń) dla wizytówek nieprzejętych */}
      <BusinessUnclaimedBanner slug={slug} entityName={biz.name} business={biz} />

      {/* Breadcrumbs — powrót do katalogu / kategoria / miasto */}
      <Breadcrumbs
        theme={biz.theme === 'light' ? 'light' : 'dark'}
        items={[
          { label: 'Katalog firm', href: '/katalog' },
          biz.category_slug ? { label: biz.category, href: `/katalog/${biz.category_slug}` } : null,
          biz.category_slug && biz.city_slug ? { label: biz.city, href: `/katalog/${biz.category_slug}/${biz.city_slug}` } : (biz.city_slug ? { label: biz.city, href: `/katalog/miasto/${biz.city_slug}` } : null),
          { label: biz.name },
        ].filter(Boolean)}
      />

      <div style={{ ...vars, minHeight: '100vh', background: 'var(--pb-bg)', color: 'var(--pb-text)', fontFamily: 'var(--pb-sans)', overflowX: 'hidden' }} data-testid="public-business-page">

        {/* ===== STICKY NAV ===== */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          background: scrolled ? 'var(--pb-nav-bg)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--pb-border)' : '1px solid transparent',
          transition: 'all 0.4s ease',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <a href="#start" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'var(--pb-serif)', fontSize: '18px', fontWeight: 500, color: 'var(--pb-text)', textDecoration: 'none', letterSpacing: '-0.01em' }} data-testid="nav-logo">
              {biz.logo && <img src={getImageUrl(biz.logo)} alt={biz.name} style={{ height: '32px', width: 'auto', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />}
              {biz.name}
            </a>

            {/* Desktop nav */}
            <div style={{ alignItems: 'center', gap: '36px' }} className="hidden md:flex">
              {navItems.map(item => (
                <a key={item.id} href={`#${item.id}`} style={{ fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--pb-text-muted)', textDecoration: 'none', transition: 'color 0.3s', fontFamily: 'var(--pb-sans)' }}
                  onMouseEnter={e => e.target.style.color = 'var(--pb-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--pb-text-muted)'}
                  data-testid={`nav-${item.id}`}>
                  {item.label}
                </a>
              ))}
            </div>

            {/* Mobile hamburger */}
            <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden" style={{ padding: '8px', color: 'var(--pb-text)', background: 'none', border: 'none', cursor: 'pointer' }} data-testid="nav-mobile-toggle">
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenu && (
            <div style={{ background: 'var(--pb-bg-alt)', borderTop: '1px solid var(--pb-border)', padding: '20px 24px' }} className="md:hidden">
              {navItems.map(item => (
                <a key={item.id} href={`#${item.id}`} onClick={() => setMobileMenu(false)}
                  style={{ display: 'block', padding: '10px 0', fontSize: '12px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--pb-text-muted)', textDecoration: 'none' }}
                  data-testid={`nav-mobile-${item.id}`}>
                  {item.label}
                </a>
              ))}
            </div>
          )}
        </nav>

        {/* ===== HERO ===== */}
        <section id="start" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 24px 60px' }}>
          {biz.cover_photo && (
            <>
              <div style={{ position: 'absolute', inset: 0 }}>
                <img src={getImageUrl(biz.cover_photo)} alt={bizImageAlt || biz.name} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isDark ? 0.12 : 0.08 }} />
              </div>
              <div style={{ position: 'absolute', inset: 0, background: isDark
                ? 'linear-gradient(to bottom, rgba(6,6,6,0.5) 0%, transparent 40%, rgba(6,6,6,0.95) 85%)'
                : 'linear-gradient(to bottom, rgba(250,250,248,0.6) 0%, transparent 40%, rgba(250,250,248,0.95) 85%)' }} />
            </>
          )}

          <FadeSection style={{ position: 'relative', zIndex: 10, maxWidth: '800px' }}>
            {/* Thin line decoration */}
            <div style={{ width: '48px', height: '1px', background: 'var(--pb-accent)', margin: '0 auto 32px', opacity: 0.6 }} />

            <p style={{ ...labelStyle, marginBottom: '24px' }}>{biz.industry}</p>

            <h1 style={{
              fontFamily: 'var(--pb-serif)',
              fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
              fontWeight: 400,
              lineHeight: 1.05,
              letterSpacing: '-0.03em',
              margin: '0 0 28px 0',
              color: 'var(--pb-text)',
            }} data-testid="hero-title">
              {biz.name}
            </h1>

            {biz.short_description && (
              <p style={{ ...bodyText, fontSize: '17px', maxWidth: '560px', margin: '0 auto 48px', lineHeight: 1.7 }}>
                {biz.short_description}
              </p>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
              {allServices.length > 0 && (
                <a href="#uslugi" style={{
                  padding: '16px 40px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                  background: 'var(--pb-accent)', color: isDark ? '#060606' : '#fff',
                  textDecoration: 'none', transition: 'all 0.3s ease', fontFamily: 'var(--pb-sans)',
                }} data-testid="hero-services-cta"
                  onMouseEnter={e => { e.target.style.background = 'var(--pb-accent-hover)'; e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.target.style.background = 'var(--pb-accent)'; e.target.style.transform = 'translateY(0)'; }}
                >Poznaj ofertę</a>
              )}
              <a href="#kontakt" style={{
                padding: '16px 40px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                background: 'transparent', color: 'var(--pb-text)', textDecoration: 'none',
                border: '1px solid var(--pb-text-dim)', transition: 'all 0.3s ease', fontFamily: 'var(--pb-sans)',
              }} data-testid="hero-contact-cta"
                onMouseEnter={e => { e.target.style.borderColor = 'var(--pb-text)'; e.target.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.target.style.borderColor = 'var(--pb-text-dim)'; e.target.style.transform = 'translateY(0)'; }}
              >Kontakt</a>
            </div>

            {biz.city && (
              <p style={{ ...bodyText, marginTop: '64px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--pb-text-dim)' }}>
                <MapPin style={{ width: '14px', height: '14px' }} /> {biz.city}{biz.address ? `, ${biz.address}` : ''}
              </p>
            )}
          </FadeSection>
        </section>

        {/* ===== DIVIDER ===== */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ height: '1px', background: 'var(--pb-border)' }} />
        </div>

        {/* ===== DYNAMIC SECTIONS (based on section_order) ===== */}
        {sectionOrder.map(sec => {
          if (sec === 'uslugi' && allServices.length > 0) return (
            <section key="uslugi" id="uslugi" style={{ ...sectionPad }}>
              <FadeSection>
                <p style={labelStyle}>Oferta</p>
                <h2 style={h2Style}>Nasze usługi</h2>
              </FadeSection>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                {allServices.map((svc, idx) => (
                  <FadeSection key={svc.id || idx} style={{ transitionDelay: `${idx * 80}ms` }}>
                    <div style={{
                      background: 'var(--pb-bg-card)', border: '1px solid var(--pb-border)', padding: '36px 32px',
                      transition: 'border-color 0.4s ease, transform 0.3s ease', cursor: 'default',
                    }} data-testid={`service-card-${idx}`}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--pb-border-hover)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--pb-border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      {svc.is_featured && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '10px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--pb-accent)', marginBottom: '16px', fontFamily: 'var(--pb-sans)' }}>
                          <Star style={{ width: '12px', height: '12px' }} /> Popularne
                        </span>
                      )}
                      <h3 style={{ fontFamily: 'var(--pb-serif)', fontSize: '22px', fontWeight: 500, color: 'var(--pb-text)', marginBottom: '12px', lineHeight: 1.3 }}>{svc.name}</h3>
                      {svc.description && <p style={{ ...bodyText, fontSize: '14px', marginBottom: '24px' }}>{svc.description}</p>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid var(--pb-border)' }}>
                        <span style={{ fontFamily: 'var(--pb-serif)', fontSize: '18px', fontWeight: 500, color: 'var(--pb-accent)' }}>
                          {svc.price_type === 'free' ? 'Bezplatnie' :
                           svc.price_type === 'contact' ? 'Wycena' :
                           svc.price_type === 'from' ? `od ${svc.price}` :
                           svc.price_type === 'hourly' ? `${svc.price}/h` :
                           svc.price || ''}
                        </span>
                        <a href="#kontakt" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--pb-text-dim)', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'color 0.3s' }}
                          onMouseEnter={e => e.target.style.color = 'var(--pb-accent)'}
                          onMouseLeave={e => e.target.style.color = 'var(--pb-text-dim)'}
                        >Zapytaj <ChevronRight style={{ width: '14px', height: '14px' }} /></a>
                      </div>
                    </div>
                  </FadeSection>
                ))}
              </div>
            </section>
          );

          if (sec === 'galeria' && hasPhotos) return (
            <section key="galeria" id="galeria">
              <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
                <div style={{ height: '1px', background: 'var(--pb-border)' }} />
              </div>
              <div style={sectionPad}>
                <FadeSection>
                  <p style={labelStyle}>Portfolio</p>
                  <h2 style={h2Style}>Nasze realizacje</h2>
                </FadeSection>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                  {biz.photos.map((photo, idx) => (
                    <FadeSection key={idx} style={{ transitionDelay: `${idx * 60}ms` }}>
                      <div style={{ aspectRatio: '4/3', overflow: 'hidden', cursor: 'pointer', position: 'relative' }}
                        onClick={() => setLightbox(idx)} data-testid={`gallery-photo-${idx}`}>
                        <img src={getImageUrl(photo)} alt={`${biz.name}${biz.city ? ' – ' + biz.city : ''} — zdjęcie ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1)' }}
                          loading="lazy"
                          decoding="async"
                          onMouseEnter={e => { e.target.style.transform = 'scale(1.06)'; }}
                          onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }} />
                      </div>
                    </FadeSection>
                  ))}
                </div>
              </div>
            </section>
          );

          if (sec === 'o-nas' && biz.description) return (
            <section key="o-nas" id="o-nas" style={{ background: 'var(--pb-bg-alt)' }}>
              <div style={{ ...sectionPad, maxWidth: '800px', textAlign: 'center' }}>
                <FadeSection>
                  <div style={{ width: '48px', height: '1px', background: 'var(--pb-accent)', margin: '0 auto 32px', opacity: 0.6 }} />
                  <p style={labelStyle}>O firmie</p>
                  <h2 style={{ ...h2Style, fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', marginBottom: '40px' }}>{biz.name}</h2>
                  <p style={{ ...bodyText, fontSize: '16px', whiteSpace: 'pre-line', lineHeight: 1.85 }}>{biz.description}</p>
                  <a href="#kontakt" style={{
                    display: 'inline-block', marginTop: '48px', padding: '16px 44px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                    border: '1px solid var(--pb-text-dim)', color: 'var(--pb-text)', textDecoration: 'none', transition: 'all 0.3s ease',
                  }}
                    onMouseEnter={e => { e.target.style.borderColor = 'var(--pb-accent)'; e.target.style.color = 'var(--pb-accent)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'var(--pb-text-dim)'; e.target.style.color = 'var(--pb-text)'; }}
                    data-testid="about-cta"
                  >Skontaktuj sie z nami</a>
                </FadeSection>
              </div>
            </section>
          );

          if (sec === 'kontakt') return (
            <section key="kontakt" id="kontakt">
              <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
                <div style={{ height: '1px', background: 'var(--pb-border)' }} />
              </div>
              <div style={sectionPad}>
                <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: '64px' }}>
                  {isUnclaimed ? (
                  <FadeSection>
                    <p style={labelStyle}>Kontakt</p>
                    <h2 style={h2Style}>Skontaktuj się z firmą</h2>
                    <p style={{ ...bodyText, marginBottom: '24px' }} data-testid="unclaimed-contact-note">
                      To <strong style={{ color: 'var(--pb-text)' }}>nieoficjalna wizytówka informacyjna</strong> — zapytania wysłane tutaj nie trafiają do firmy. Skontaktuj się bezpośrednio, korzystając z danych kontaktowych obok{biz.website ? ' lub przez stronę firmy' : ''}.
                    </p>
                    {biz.website && (
                      <a
                        href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="unclaimed-website-link"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '16px 28px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', background: 'var(--pb-accent)', color: isDark ? '#060606' : '#fff', textDecoration: 'none' }}
                      >
                        Przejdź na stronę firmy
                      </a>
                    )}
                    <p style={{ ...bodyText, fontSize: '13px', marginTop: '28px', opacity: 0.85 }}>
                      Jesteś właścicielem? Możesz <strong style={{ color: 'var(--pb-text)' }}>przejąć, poprawić lub usunąć</strong> tę wizytówkę — użyj przycisków na górze strony.
                    </p>
                    <div style={{ marginTop: '28px', maxWidth: '460px' }}>
                      <UnclaimedInterest entityType="business" slug={slug} kind="firma" />
                    </div>
                  </FadeSection>
                  ) : (
                  <FadeSection>
                    <p style={labelStyle}>Kontakt</p>
                    <h2 style={h2Style}>Napisz do nas</h2>
                    {sent ? (
                      <div style={{ background: 'var(--pb-bg-card)', border: '1px solid var(--pb-border)', padding: '64px 32px', textAlign: 'center' }} data-testid="inquiry-sent">
                        <Check style={{ width: '48px', height: '48px', color: 'var(--pb-accent)', margin: '0 auto 16px' }} />
                        <h3 style={{ fontFamily: 'var(--pb-serif)', fontSize: '24px', fontWeight: 500, marginBottom: '8px', color: 'var(--pb-text)' }}>Wiadomosc wyslana!</h3>
                        <p style={bodyText}>Odpowiemy tak szybko, jak to mozliwe.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="inquiry-form">
                        <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))}
                          placeholder="Twoje imie *"
                          style={{ width: '100%', padding: '16px 20px', fontSize: '14px', background: 'var(--pb-input-bg)', border: '1px solid var(--pb-input-border)', color: 'var(--pb-text)', fontFamily: 'var(--pb-sans)', outline: 'none', transition: 'border-color 0.3s' }}
                          onFocus={e => e.target.style.borderColor = 'var(--pb-accent)'}
                          onBlur={e => e.target.style.borderColor = 'var(--pb-input-border)'}
                          data-testid="inquiry-name" />
                        <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '16px' }}>
                          <input value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))}
                            placeholder="Email" type="email"
                            style={{ width: '100%', padding: '16px 20px', fontSize: '14px', background: 'var(--pb-input-bg)', border: '1px solid var(--pb-input-border)', color: 'var(--pb-text)', fontFamily: 'var(--pb-sans)', outline: 'none', transition: 'border-color 0.3s' }}
                            onFocus={e => e.target.style.borderColor = 'var(--pb-accent)'}
                            onBlur={e => e.target.style.borderColor = 'var(--pb-input-border)'}
                            data-testid="inquiry-email" />
                          <input value={formData.phone} onChange={e => setFormData(p => ({...p, phone: e.target.value}))}
                            placeholder="Telefon"
                            style={{ width: '100%', padding: '16px 20px', fontSize: '14px', background: 'var(--pb-input-bg)', border: '1px solid var(--pb-input-border)', color: 'var(--pb-text)', fontFamily: 'var(--pb-sans)', outline: 'none', transition: 'border-color 0.3s' }}
                            onFocus={e => e.target.style.borderColor = 'var(--pb-accent)'}
                            onBlur={e => e.target.style.borderColor = 'var(--pb-input-border)'}
                            data-testid="inquiry-phone" />
                        </div>
                        <textarea value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))}
                          placeholder="W czym mozemy pomoc?" rows={5}
                          style={{ width: '100%', padding: '16px 20px', fontSize: '14px', background: 'var(--pb-input-bg)', border: '1px solid var(--pb-input-border)', color: 'var(--pb-text)', fontFamily: 'var(--pb-sans)', outline: 'none', resize: 'none', transition: 'border-color 0.3s' }}
                          onFocus={e => e.target.style.borderColor = 'var(--pb-accent)'}
                          onBlur={e => e.target.style.borderColor = 'var(--pb-input-border)'}
                          data-testid="inquiry-message" />
                        <button type="submit" disabled={sending}
                          style={{
                            width: '100%', padding: '18px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                            background: 'var(--pb-accent)', color: isDark ? '#060606' : '#fff', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            fontFamily: 'var(--pb-sans)', transition: 'all 0.3s ease',
                            opacity: sending ? 0.7 : 1,
                          }} data-testid="inquiry-submit"
                          onMouseEnter={e => { if (!sending) { e.target.style.background = 'var(--pb-accent-hover)'; e.target.style.transform = 'translateY(-2px)'; } }}
                          onMouseLeave={e => { e.target.style.background = 'var(--pb-accent)'; e.target.style.transform = 'translateY(0)'; }}
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send style={{ width: '14px', height: '14px' }} />}
                          Wyślij wiadomość
                        </button>
                      </form>
                    )}
                  </FadeSection>
                  )}
                  <FadeSection style={{ transitionDelay: '150ms' }}>
                    <p style={labelStyle}>Dane kontaktowe</p>
                    <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {biz.phone && (
                        <a href={`tel:${biz.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--pb-text-muted)', textDecoration: 'none', fontSize: '16px', transition: 'color 0.3s', fontFamily: 'var(--pb-sans)' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--pb-text)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--pb-text-muted)'}
                          onClick={() => track('tel_click', { entity_type: 'business', entity_id: biz.id, slug: biz.slug, user_id: biz.user_id || '', name: biz.name })}
                          data-testid="contact-phone">
                          <div style={{ width: '44px', height: '44px', border: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Phone style={{ width: '16px', height: '16px' }} /></div>
                          {biz.phone}
                        </a>
                      )}
                      {biz.email && (
                        <a href={`mailto:${biz.email}`} style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--pb-text-muted)', textDecoration: 'none', fontSize: '15px', transition: 'color 0.3s', fontFamily: 'var(--pb-sans)' }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--pb-text)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--pb-text-muted)'}
                          onClick={() => track('email_click', { entity_type: 'business', entity_id: biz.id, slug: biz.slug, user_id: biz.user_id || '', name: biz.name })}
                          data-testid="contact-email">
                          <div style={{ width: '44px', height: '44px', border: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Mail style={{ width: '16px', height: '16px' }} /></div>
                          {biz.email}
                        </a>
                      )}
                      {(biz.address || biz.city) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--pb-text-muted)', fontSize: '15px', fontFamily: 'var(--pb-sans)' }} data-testid="contact-address">
                          <div style={{ width: '44px', height: '44px', border: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <MapPin style={{ width: '16px', height: '16px' }} /></div>
                          {[biz.address, biz.city].filter(Boolean).join(', ')}
                        </div>
                      )}
                      {biz.opening_hours && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'var(--pb-text-muted)', fontSize: '15px', fontFamily: 'var(--pb-sans)' }} data-testid="contact-hours">
                          <div style={{ width: '44px', height: '44px', border: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Clock style={{ width: '16px', height: '16px' }} /></div>
                          {biz.opening_hours}
                        </div>
                      )}
                    </div>
                    {(biz.social_facebook || biz.social_instagram) && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '48px' }}>
                        {biz.social_facebook && (
                          <a href={biz.social_facebook} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '12px 24px', fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', border: '1px solid var(--pb-border)', color: 'var(--pb-text-muted)', textDecoration: 'none', transition: 'all 0.3s', fontFamily: 'var(--pb-sans)' }}
                            onMouseEnter={e => { e.target.style.borderColor = 'var(--pb-border-hover)'; e.target.style.color = 'var(--pb-text)'; }}
                            onMouseLeave={e => { e.target.style.borderColor = 'var(--pb-border)'; e.target.style.color = 'var(--pb-text-muted)'; }}
                            data-testid="social-facebook">Facebook</a>
                        )}
                        {biz.social_instagram && (
                          <a href={biz.social_instagram} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '12px 24px', fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', border: '1px solid var(--pb-border)', color: 'var(--pb-text-muted)', textDecoration: 'none', transition: 'all 0.3s', fontFamily: 'var(--pb-sans)' }}
                            onMouseEnter={e => { e.target.style.borderColor = 'var(--pb-border-hover)'; e.target.style.color = 'var(--pb-text)'; }}
                            onMouseLeave={e => { e.target.style.borderColor = 'var(--pb-border)'; e.target.style.color = 'var(--pb-text-muted)'; }}
                            data-testid="social-instagram">Instagram</a>
                        )}
                      </div>
                    )}
                  </FadeSection>
                </div>
              </div>
            </section>
          );

          return null;
        })}

        {/* ===== FAQ (SEO + użytkownicy) ===== */}
        {faqItems.length > 0 && (
          <section id="faq" style={{ background: 'var(--pb-bg-alt)' }} data-testid="faq-section">
            <div style={{ ...sectionPad, maxWidth: '820px' }}>
              <FadeSection>
                <p style={labelStyle}>FAQ</p>
                <h2 style={{ ...h2Style, marginBottom: '40px' }}>Najczęstsze pytania</h2>
              </FadeSection>
              <div>
                {faqItems.map((it, i) => (
                  <FadeSection key={i} style={{ transitionDelay: `${i * 60}ms` }}>
                    <div style={{ borderTop: '1px solid var(--pb-border)', padding: '28px 0' }} data-testid={`faq-item-${i}`}>
                      <h3 style={{ fontFamily: 'var(--pb-serif)', fontSize: '20px', fontWeight: 500, color: 'var(--pb-text)', margin: '0 0 12px 0', lineHeight: 1.35 }}>{it.q}</h3>
                      <p style={{ ...bodyText, fontSize: '15px', margin: 0, whiteSpace: 'pre-line' }}>{it.a}</p>
                    </div>
                  </FadeSection>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ===== POWIĄZANE FIRMY (linkowanie wewnętrzne / SEO) ===== */}
        {Array.isArray(similar) && similar.length > 0 && (
          <section id="powiazane" style={{ background: 'var(--pb-bg)' }} data-testid="related-section">
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
              <div style={{ height: '1px', background: 'var(--pb-border)' }} />
            </div>
            <div style={sectionPad}>
              <FadeSection>
                <p style={labelStyle}>Zobacz też</p>
                <h2 style={{ ...h2Style, marginBottom: '40px' }}>Powiązane firmy</h2>
              </FadeSection>
              {similar.map((group, gi) => (
                <div key={group.key || gi} style={{ marginBottom: '48px' }} data-testid={`related-group-${group.key}`}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
                    <h3 style={{ fontFamily: 'var(--pb-serif)', fontSize: '20px', fontWeight: 500, color: 'var(--pb-text)', margin: 0 }}>{group.title}</h3>
                    {group.hub_url && (
                      <a href={group.hub_url} style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--pb-text-dim)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'color 0.3s' }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--pb-accent)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--pb-text-dim)'}
                        data-testid={`related-hub-${group.key}`}
                      >Zobacz wszystkie <ChevronRight style={{ width: '14px', height: '14px' }} /></a>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                    {group.items.map((it, ii) => (
                      <a key={it.slug || ii} href={it.url}
                        style={{ display: 'block', background: 'var(--pb-bg-card)', border: '1px solid var(--pb-border)', padding: '18px 20px', textDecoration: 'none', transition: 'border-color 0.3s, transform 0.3s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--pb-border-hover)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--pb-border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        data-testid={`related-card-${it.slug}`}>
                        <span style={{ display: 'block', fontFamily: 'var(--pb-serif)', fontSize: '16px', fontWeight: 500, color: 'var(--pb-text)', marginBottom: '6px', lineHeight: 1.3 }}>{it.name}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--pb-text-dim)', fontFamily: 'var(--pb-sans)' }}>
                          {it.city && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><MapPin style={{ width: '12px', height: '12px' }} /> {it.city}</span>}
                          {it.google_rating ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--pb-accent)' }}><Star style={{ width: '12px', height: '12px' }} /> {it.google_rating}</span> : null}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== FOOTER ===== */}
        <footer style={{ background: 'var(--pb-bg)', borderTop: '1px solid var(--pb-border)' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '64px 24px 48px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '48px' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--pb-serif)', fontSize: '18px', fontWeight: 500, marginBottom: '8px', color: 'var(--pb-text)' }}>{biz.name}</h3>
                <p style={{ fontSize: '13px', color: 'var(--pb-text-dim)', lineHeight: 1.6 }}>{biz.industry}{biz.city ? ` — ${biz.city}` : ''}</p>
              </div>
              <div>
                <p style={{ ...labelStyle, fontSize: '10px', marginBottom: '12px' }}>Kontakt</p>
                {biz.phone && <p style={{ fontSize: '13px', color: 'var(--pb-text-dim)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}><Phone style={{ width: '12px', height: '12px' }} /> {biz.phone}</p>}
                {biz.email && <p style={{ fontSize: '13px', color: 'var(--pb-text-dim)', display: 'flex', alignItems: 'center', gap: '8px' }}><Mail style={{ width: '12px', height: '12px' }} /> {biz.email}</p>}
              </div>
              <div>
                <p style={{ ...labelStyle, fontSize: '10px', marginBottom: '12px' }}>Nawigacja</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
                  {navItems.map(item => (
                    <a key={item.id} href={`#${item.id}`} style={{ fontSize: '13px', color: 'var(--pb-text-dim)', textDecoration: 'none', transition: 'color 0.3s' }}
                      onMouseEnter={e => e.target.style.color = 'var(--pb-text-muted)'}
                      onMouseLeave={e => e.target.style.color = 'var(--pb-text-dim)'}
                      data-testid={`footer-nav-${item.id}`}
                    >{item.label}</a>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: 'var(--pb-text-dim)' }}>{biz.name} &copy; {new Date().getFullYear()}</span>
              <a href="https://apisai.pl" target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: 'var(--pb-text-dim)', textDecoration: 'none', transition: 'color 0.3s' }}
                onMouseEnter={e => e.target.style.color = 'var(--pb-accent)'}
                onMouseLeave={e => e.target.style.color = 'var(--pb-text-dim)'}
                data-testid="footer-apisai"
              >Stworzone z ApisAI</a>
            </div>
          </div>
        </footer>

        {/* ===== SCROLL TO TOP ===== */}
        {scrolled && (
          <button onClick={scrollToTop}
            style={{
              position: 'fixed', bottom: '80px', right: '24px', zIndex: 40,
              width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--pb-bg-card)', border: '1px solid var(--pb-border)', cursor: 'pointer',
              color: 'var(--pb-text-muted)', transition: 'all 0.3s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--pb-accent)'; e.currentTarget.style.color = 'var(--pb-accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--pb-border)'; e.currentTarget.style.color = 'var(--pb-text-muted)'; }}
            data-testid="scroll-to-top"
          >
            <ArrowUp style={{ width: '16px', height: '16px' }} />
          </button>
        )}
      </div>
    </>
  );
}
